/**
 * Specsheets
 *
 * @dependencies
 * * jquery
 * *
 * * jsTranslation.getContext() (erco.js) -> Loading all translations needed for the specsheet after being async loaded
 * * globals.trans.specsheets (erco.js) -> Containing the specsheet translations and available after the jsTranslation.getContext() call
 * * ProductOverview (productoverview.js) -> Contact form initialisation in async loaded Specsheet
 * * CheckboxGroup (erco.js) -> Checkbox initialisation
 * * Globals.renderMustacheTemplates() (erco.js) -> Render untouched mustache render elements probably found in the async loaded form
 * * toolkit.downloadFile() (toolkit.js) -> Used to download planing data
 * * trackDownload() (?) -> Probably used to track downloads somewhere
 * * Collector.eventListenerAdded (myerco/myerco.js) -> Reseting the flag used to indicate if eventListeners were already added
 * * Collector.injectUI() (myerco/myerco.js) -> Used to inject the collector UI (Marking star)
 * * PopoverController (popover.js) -> Manages the positioning and behavior of popovers
 * *
 * * pdb-api route: /specsheets/internalArticleInfos/:artno/:lang/?api=v1.1
 *
 * @dependents
 * * $(document).ready(function()
 * * css-class spec-contact-form used in pdb-api -> dist/application/v1.1/views/specsheets/specsheet_contact_form.php
 * * isActive => DialogOverlays.handlePressKey
 *
 * @note
 * data attribute [data-js-specspinner] is not used in pdp-api / cms-backend / lightscout
 *
 *
 * @commit b.kelm (2018-10)
 */

var Specsheets = {
  // Specsheet article number
  artno: '',
  // Checking if a specsheet is currently active to prevent multiple from loading
  isActive: false,
  // Keeping track of the location href before showing the specsheet
  originalLocationHref: window.location.href,
  /**
   * Current certificate download section instance
   * @type {SpecsheetDownloadSection | null}
   */
  certificateDownloadSection: null,
  /**
   * Current file download section instance
   * @type {SpecsheetDownloadSection | null}
   */
  fileDownloadSection: null,
  /**
   * Wrapper element for the specsheet
   * @type {HTMLDialogElement | null}
   */
  dialogWrapperElement: null,
  /**
   * Indicates if it has been initially checked if the specsheet should be shown
   * @type {boolean}
   */
  initialChecked: false,
  // Init function
  init(artno) {
    authHelper.initJwtAuth();
    // If no specsheet is active
    if (!this.isActive) {
      this.isActive = true;
      this.artno = artno;
      Specsheets.addLoadingSpinner();
      Specsheets.fetch();
      // Fetching translations
      jsTranslation.getContext('specsheets');
    }
  },
  // Fetches the specsheet
  async fetch() {
    await Specsheets.append(this.artno);
    Specsheets.initQuery();
    productoverview.initContactForms();
    document.querySelectorAll('.spec-contact-form [data-js-check-checkboxes]').forEach((element) => {
      new CheckboxGroup(element);
    });
    globals.renderMustacheTemplates();
  },

  // Toggeling the loading spinner
  toggleLoadingSpinner() {
    var spinnerElms = document.querySelectorAll('[data-js-specspinner]');

    if (spinnerElms.length > 0) {
      for (var i = spinnerElms.length - 1; i >= 0; i--) {
        spinnerElms[i].parentNode.removeChild(spinnerElms[i]);
      }
    } else {
      var triggerElms = document.querySelectorAll('[data-artno="' + this.artno + '"][data-js-loadspecsheet]'),
        spinnerElms = [];

      // Looping all trigger elements and checking which have an image as child
      for (var i = 0; i < triggerElms.length; i++) {
        if (!triggerElms[i].querySelector('img')) {
          var imgElm = document.createElement('img');
          imgElm.src = '/struktur/img/ajax-loader-button.gif';
          imgElm.setAttribute('data-js-specspinner', '');

          triggerElms[i].appendChild(imgElm);
        }
      }
    }
  },
  /**
   * Appends a specsheet for given articleId to the page in a dialog
   * @param {string} articleId - Article to open
   */
  async append(articleId) {
    const specsheetLoadPromise = new Promise((resolve, reject) => {
      // Resolve on successfully loading the specsheet data
      window.addEventListener('specsheet-view-loaded', () => {
        resolve();
      }, { once: true });
      // Reject on error while loading the specsheet data
      window.addEventListener('specsheet-view-error', () => {
        reject();
      }, { once: true });
    });
    
    const downloadSectionReadyPromise = new Promise((resolve) => {
      window.addEventListener('specsheet-downloads-ready', () => {
        resolve();
      }, { once: true });
    });

    // Create actual specsheet with the custom vue web component
    const specsheetView = document.createElement('specsheet-view');
    specsheetView.setAttribute('article-id', articleId);
    specsheetView.setAttribute('language', k3vars.S_Sprache);
    // Close specsheet event handler
    window.addEventListener('specsheet-close', () => {
      Specsheets.close();
    });

    // Create the wrapping dialog element
    Specsheets.dialogWrapperElement = document.createElement('dialog');
    Specsheets.dialogWrapperElement.setAttribute('data-scoped-css-v3', '');
    Specsheets.dialogWrapperElement.setAttribute('data-js-specsheet', '');
    Specsheets.dialogWrapperElement.setAttribute('aria-labelledby', 'specsheet-heading');
    Specsheets.dialogWrapperElement.appendChild(specsheetView);
    document.getElementsByTagName('body')[0].appendChild(Specsheets.dialogWrapperElement);
    document.body.classList.add('er-scroll--lock');

    Promise.all([specsheetLoadPromise, downloadSectionReadyPromise])
      .then(
        // resolved
        () => {
          Specsheets.initDownloadSection();
          Specsheets.initCheckboxes();
          Specsheets.fetchCertificates();

          // init external classes on open
          window.PopoverController.init();
          window.ShareHelper.init();
          window.Collector.refresh();
          window.Offers.enableMyERCOOfferButton(document.querySelector('[data-js-myerco-offer-dynamic]'));

          // open the dialog if overlays below are opend
          Specsheets.waitForDialogsChecked().then(() => {
            // Set timestamp to track opening order
            Specsheets.dialogWrapperElement.dataset.openedAt = Date.now();

            Specsheets.dialogWrapperElement.showModal();
            Specsheets.dispatchInitialChecked();
          });
        },
        // rejected
        () => {
          console.error('An error has occured while loading the specsheet');
          Specsheets.close();
        }
      ).finally(() => {
        Specsheets.removeLoadingSpinner();
      });
  },
  // Adding the loading spinner
  addLoadingSpinner() {
    var spinnerElms = document.querySelectorAll('[data-js-specspinner]');

    var triggerElms = document.querySelectorAll('[data-artno="' + this.artno + '"][data-js-loadspecsheet]'),
      spinnerElms = [];

    // Looping all trigger elements and checking which have an image as child
    for (var i = 0; i < triggerElms.length; i++) {
      if (!triggerElms[i].querySelector('img')) {
        var imgElm = document.createElement('img');
        imgElm.src = '/struktur/img/ajax-loader-button.gif';
        imgElm.classList.add('spec-spinner');
        imgElm.setAttribute('data-js-specspinner', '');

        triggerElms[i].appendChild(imgElm);
      }
    }
  },
  // Removing the loading spinner
  removeLoadingSpinner() {
    var spinnerElms = document.querySelectorAll('[data-js-specspinner]');

    for (var i = spinnerElms.length - 1; i >= 0; i--) {
      spinnerElms[i].parentNode.removeChild(spinnerElms[i]);
    }
  },
  // Closes the specsheet
  close() {
    if (!Specsheets.dialogWrapperElement) return;

    // Close the dialog
    Specsheets.dialogWrapperElement.close();

    // Removing the popState event listener
    window.removeEventListener('popstate', specBackClose);
    // Removing the query parameters from the URL
    Specsheets.removeQuery();
    // Reverting the body scroll prevent
    if (document.querySelectorAll('dialog[open]').length === 0) {
      document.body.classList.remove('er-scroll--lock');
    }
    // Setting the specsheet state to inactive
    this.isActive = false;


    // Removing the specsheet element from the DOM
    Specsheets.dialogWrapperElement.remove();
    Specsheets.dialogWrapperElement = null;
  },
  // Removing the specsheet query parameters
  removeQuery() {
    let url = this.originalLocationHref;
    url = window.toolkit.removeQueryParameter('specsheet', url);
    url = window.toolkit.removeQueryParameter('articles', url);

    window.history.pushState(null, '', url);
  },
  // Initializing all neccesary query functions
  initQuery() {
    Specsheets.appendQuery();
    Specsheets.addBackListener();
  },
  // Appending a query to the site URL
  appendQuery() {
    var url = window.location.href,
      specQuery = window.toolkit.getQueryParameter('specsheet', url),
      artQuery = window.toolkit.getQueryParameter('articles', url);

    // Checking if there is already an appropriate query (happens if it is already present on pageload)
    if (!artQuery && !specQuery) {
      // Storing the current url to be able to jump back to it, for when the specsheet is being closed
      this.originalLocationHref = url;

      var newTemporarySpecsheetUrl = window.location.origin + '/' + k3vars.S_Sprache + '/';

      window.history.pushState(
        null,
        this.artno,
        window.toolkit.addQueryParameter('specsheet', this.artno, newTemporarySpecsheetUrl)
      );
    }
  },
  // Adding an event listener to catch the back-button event
  addBackListener() {
    window.addEventListener(
      'popstate',
      (specBackClose = function (e) {
        e.preventDefault();
        Specsheets.close();

        // Open specsheet if query is present
        Specsheets.checkQuery();
      })
    );
  },
  // Checking if a query exists on pageload
  checkQuery() {
    var artno = window.toolkit.getQueryParameter('specsheet', window.location.href),
      artno_alt = window.toolkit.getQueryParameter('articles', window.location.href);

    if (artno || artno_alt) {
      // Open the specsheet
      Specsheets.init(artno || artno_alt);
    } else {
      // No specsheet to be opened
      Specsheets.dispatchInitialChecked();
    }
  },
  // Downloading a single element from a specsheet
  downloadSingleFile({ id, type }) {
    if (id == 15) {
      // Now handled in specsheet frontend vue package
    } else {
      Specsheets.downloadSpecItems([id]);
    }
    Specsheets.sendAnalyticsData([type]);
  },
  downloadMultipleFiles(downloads) {
    const { ids, types } = downloads.reduce(
      (acc, download) => {
        acc.ids.push(download.id);
        acc.types.push(download.type);
        return acc;
      },
      { ids: [], types: [] }
    );

    if (ids) {
      if (ids.length === 1 && ids[0] == 15) {
        Specsheets.generateSpecsheet();
      } else {
        Specsheets.downloadSpecItems(ids);
      }
      Specsheets.sendAnalyticsData(types);
    }
  },
  async downloadSpecItems(ids) {
    // Set the downloadspinner
    this.fileDownloadSection.disableDownloadButton();
    this.fileDownloadSection.showSpinner();

    // Create URL
    const url =
      confEnv['download_service_url'] +
      '/download/' +
      this.artno +
      '/' +
      k3vars.S_Sprache +
      '/?types=' +
      ids.join() +
      '&formattedId&productType=article';

    // try the download
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(response.status);
      }

      const blob = await response.blob();
      const bloblUrl = window.URL.createObjectURL(blob);
      const filename = response.headers.get('Content-Disposition').match(/filename="(?<zipName>.*?)"/).groups.zipName;

      window.toolkit.downloadFile(bloblUrl, true, filename);
    } catch (error) {
      // Just log the error if we have no instance of our download section
      if (!this.fileDownloadSection) return console.error(error);

      let message =
        globals.trans.specsheets['es_ist_ein_fehler_aufgetreten'] || 'An error occured while downloading the file';

      // Show a new message box
      this.fileDownloadSection.newMessageBox(message, 'danger');
    }
    // Remove the downloadspinner
    this.fileDownloadSection.enableDownloadButton();
    this.fileDownloadSection.hideSpinner();
  },
  // Sending analytic data to Google
  sendAnalyticsData(data) {
    if (typeof trackDownload == 'function') {
      for (var i = 0; i < data.length; i++) {
        var analyticString =
          'download/' + this.artno + '/' + k3vars.S_Sprache + '/' + new Date().getFullYear() + '/' + data[i];
        trackDownload(analyticString);
      }
    }
  },
  initDownloadSection() {
    const section = document.querySelector('#specsheet .specsheet-downloads');
    this.fileDownloadSection = new SpecsheetDownloadSection(
      section,
      Specsheets.downloadMultipleFiles,
      Specsheets.downloadSingleFile
    );
  },
  initCheckboxes() {
    // This forces the Collector to setup the new listeners.
    // Otherwise the listeners dont work on the specsheet popup ^schi
    Collector.eventListenerAdded = false;
    Collector.injectUI($('#specsheet'));
  },
  generateSpecsheet() {
    document.querySelector('#pdfSpecGenerationBtn').click();
  },
  /**
   * Download a single Certificate
   * @param {SelectedDownload} download - Selected download
   */
  downloadSingleCertificate(download) {
    Specsheets.downloadMultipleCertificates([download]);
  },
  /**
   * Download multiple certificates
   * @param {SelectedDownload[]} downloads - Selected downloads
   */
  async downloadMultipleCertificates(downloads) {
    const certs = downloads.map(({ id }) => id).join(',');
    const url = `${confEnv.download_service_url}/certificates/${this.artno}/${k3vars.S_Sprache}?certs=${certs}&formattedId`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(response.status);
      }

      const blob = await response.blob();
      const bloblUrl = window.URL.createObjectURL(blob);
      const filename = response.headers.get('Content-Disposition').match(/filename="(?<certName>.*?)"/).groups.certName;

      window.toolkit.downloadFile(bloblUrl, true, filename);
    } catch (error) {
      // Just log the error if we have no instance of our download section
      if (!this.certificateDownloadSection) return console.error(error);

      // Get translation for certs
      let translations = globals?.trans?.certificates;

      if (!translations) {
        await new Promise((resolve) => {
          jsTranslation.getContext('certificates', resolve);
        }).then(() => {
          translations = globals.trans.certificates;
        });
      }

      // Set proper message depending on error
      let message = 'An error occured while downloading the certificate';
      if (error.message === '404') {
        message = translations.zertifikate_download_fehler || 'Document not found, please contact I/RD/TC';
      }

      // Show a new message box
      this.certificateDownloadSection.newMessageBox(message, 'danger');
    }
  },
  fetchCertificates() {
    authHelper.isStaff
      .then((isStaff) => {
        if (!isStaff) throw new Error('User is not staff');

        const url = `${confEnv['specsheet_service_url']}/certificates/${this.artno}/${k3vars.S_Sprache}`;

        return fetch(url, {
          credentials: 'include',
        });
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }

        return response.text();
      })
      .then((html) => {
        const downloadSection = document.querySelector('#specsheet .specsheet-downloads');
        const fragment = document.createRange().createContextualFragment(html);
        this.certificateDownloadSection = new SpecsheetDownloadSection(
          fragment,
          Specsheets.downloadMultipleCertificates.bind(this),
          Specsheets.downloadSingleCertificate
        );

        downloadSection.after(fragment);
      })
      .catch((err) => {
        // Do nothing if we get our own is not staff error, cause we dont want to
        // show the cert section, so that error is expected
        if (err.message === 'User is not staff') return;
        console.error(err);
      });
  },

  dispatchInitialChecked() {
    Specsheets.initialChecked = true;
    document.dispatchEvent(new Event('specsheet:checked'));
  },

  // Function returns a promise that resolves if all events for overlays have been initially checked
  waitForDialogsChecked: function () {
    return new Promise((resolve) => {
      const allDone = () => window.productHeaderTooltip.initialOverlayChecked;

      if (allDone()) {
        resolve();
        return;
      }

      const onAny = () => {
        if (allDone()) {
          cleanup();
          resolve();
        }
      };
      document.addEventListener('phaseoutOverlay:checked', onAny);

      const cleanup = () => {
        document.removeEventListener('phaseoutOverlay:checked', onAny);
      };
    });
  },
};

window.Specsheets = Specsheets;
